// ═══════════════════════════════════════════════════════════════════════
// Module 1 — Liste professionnelle des salariés (première livraison).
// Recherche, filtres, vues, tri et pagination côté serveur (GET /api/salaries) ; population du
// Module 0 appliquée par le serveur ; la ligne ouvre le dossier. L'état de la liste vit dans
// l'URL : le retour depuis une fiche retrouve exactement la même vue.
// ═══════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import {
  UserPlus, Download, Users, SearchX, ChevronRight, ChevronLeft, RotateCcw, Lock, Layers, EllipsisVertical, Search, SlidersHorizontal, X,
  List, LayoutGrid, Briefcase, Building2, UserRound, Filter,
} from 'lucide-react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import {
  Bouton, BandeauInfo, Selecteur, Pagination, EtatVide, Squelette, MenuActions,
  Avatar, BadgeStatut, BadgeDossier, formaterDate, initiales,
} from '../design/composants';
import { useFichierSecurise } from '../utils/fichiersSecurises';

const ROLES_CREATION = ['drh', 'rh_principal', 'assistant_rh'];
const LIBELLE_MENU = {
  prepose_regional: 'Mon site', responsable_site: 'Mon site', chef_departement: 'Mon département',
  chef_service: 'Mon service', chef_equipe: 'Mon équipe',
};
const VUES = [
  { cle: 'actifs', libelle: 'Actifs' },
  { cle: 'dossier_incomplet', libelle: 'Dossier incomplet', qualite: true },
  { cle: 'sans_responsable', libelle: 'Sans responsable' },
  { cle: 'cdd_echeance', libelle: 'CDD à échéance (60 jours)' },
  { cle: 'suspendus', libelle: 'Suspendus' },
  { cle: 'sortis', libelle: 'Sortis' },
  { cle: 'tous', libelle: 'Tous' },
];
const TRIS = [
  { valeur: 'nom', libelle: 'Nom' },
  { valeur: 'matricule', libelle: 'Matricule' },
  { valeur: 'entree_recente', libelle: 'Entrée la plus récente' },
  { valeur: 'dossier', libelle: 'Dossier à traiter d\'abord', qualite: true },
];
const LIBELLE_STATUT = { Retraite: 'Retraité' };
const FILTRES = ['q', 'site_id', 'departement_id', 'service_id', 'metier_id', 'statut', 'type_contrat', 'dossier'];
const MEMO_LISTE = 'sogas_liste_salaries';
const MEMO_AFFICHAGE = 'sogas_affichage_salaries';
const LIBELLE_DOSSIER = { bloquant: 'Bloquant', a_completer: 'À compléter', complet: 'Complet', clos: 'Clos (sortis)' };

/** Vue en cartes : photo ou initiales, nom, poste, affectation, responsable, statut et dossier. */
function CarteSalarie({ l, avecQualite, onOuvrir }) {
  const url = useFichierSecurise(l.photo_url || null);
  return (
    <li className={`rh-carte-salarie ${l.ouvrable ? '' : 'rh-ligne-inactive'}`} tabIndex={l.ouvrable ? 0 : -1}
      role={l.ouvrable ? 'link' : undefined} aria-label={l.ouvrable ? `Ouvrir le dossier de ${nomComplet(l)}` : undefined}
      title={l.ouvrable ? undefined : 'La fiche de ce salarié ne vous est pas accessible'}
      onClick={() => onOuvrir(l)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOuvrir(l); } }}>
      <div className="rh-carte-salarie-visuel" aria-hidden="true">{url ? <img src={url} alt="" /> : initiales(l.prenom, l.nom)}</div>
      <div className="rh-carte-salarie-corps">
        <div className="rh-carte-salarie-nom">{nomComplet(l)}</div>
        <div className="rh-carte-salarie-ligne"><Briefcase size={14} aria-hidden="true" />
          <span style={{ color: l.intitule ? undefined : 'var(--sg-texte-4)' }}>{l.intitule || 'Métier ou poste non renseigné'}</span></div>
        <div className="rh-carte-salarie-ligne"><Building2 size={14} aria-hidden="true" /><span>{affectation(l)}</span></div>
        <div className="rh-carte-salarie-ligne"><UserRound size={14} aria-hidden="true" />
          <span>{l.responsable ? nomComplet(l.responsable) : <span style={{ color: 'var(--sg-alerte)' }}>{l.responsable_vacant ? 'Poste du responsable vacant' : 'Responsable à définir'}</span>}</span></div>
        <div className="rh-carte-salarie-pied">
          <BadgeStatut statut={l.statut} />
          {avecQualite && <BadgeDossier etat={l.dossier.etat} nbBloquant={l.dossier.nb_bloquant} nbACompleter={l.dossier.nb_a_completer} />}
        </div>
      </div>
    </li>
  );
}

/** Situation contractuelle lisible : « CDI depuis 03/2019 », « CDD jusqu'au 12/11/2026 ». */
function situationContrat(l) {
  if (l.date_sortie) return `Sorti le ${formaterDate(l.date_sortie)}`;
  if (!l.type_contrat) return 'Contrat non renseigné';
  if (l.type_contrat === 'CDD' && l.date_fin_contrat) return `CDD jusqu'au ${formaterDate(l.date_fin_contrat)}`;
  if (l.date_embauche) {
    const d = new Date(`${String(l.date_embauche).slice(0, 10)}T12:00:00`);
    return `${l.type_contrat} depuis ${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  }
  return l.type_contrat;
}
const affectation = (l) => [l.service || l.departement, l.site?.nom].filter(Boolean).join(' · ') || 'Affectation non renseignée';
const nomComplet = (l) => `${l.prenom || ''} ${(l.nom || '').toUpperCase()}`.trim();

export default function EmployesPage() {
  const { employe: user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [donnees, setDonnees] = useState(null);
  const [options, setOptions] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [saisie, setSaisie] = useState(params.get('q') || '');
  const [exportEnCours, setExportEnCours] = useState(false);
  const requete = useRef(0);

  const titre = LIBELLE_MENU[user?.role] || (user?.role === 'employe' ? 'Mon équipe' : 'Employés');
  const peutCreer = ROLES_CREATION.includes(user?.role);
  const vue = params.get('vue') || 'actifs';
  const tri = params.get('tri') || 'nom';
  const filtresActifs = FILTRES.some((f) => params.get(f));

  const majParams = useCallback((modifs, garderPage = false) => {
    const p = new URLSearchParams(params);
    Object.entries(modifs).forEach(([k, v]) => { if (v === '' || v === null || v === undefined) p.delete(k); else p.set(k, String(v)); });
    if (!garderPage) p.delete('page');
    setParams(p, { replace: true });
  }, [params, setParams]);

  // Recherche : envoyée 300 ms après la dernière frappe.
  useEffect(() => {
    if ((params.get('q') || '') === saisie.trim()) return undefined;
    const t = setTimeout(() => majParams({ q: saisie.trim() }), 300);
    return () => clearTimeout(t);
  }, [saisie]);

  useEffect(() => {
    const id = ++requete.current;
    setChargement(true);
    setErreur('');
    const query = Object.fromEntries(params.entries());
    if (!options) query.options = '1';
    axios.get('/api/salaries', { params: query })
      .then(({ data }) => {
        if (id !== requete.current) return;
        setDonnees(data);
        if (data.options) setOptions(data.options);
      })
      .catch((e) => { if (id === requete.current) setErreur(e.response?.data?.message || 'La liste des salariés n\'a pas pu être chargée.'); })
      .finally(() => { if (id === requete.current) setChargement(false); });
    // La vue courante est mémorisée pour le lien « Retour à la liste » de la fiche.
    try { sessionStorage.setItem(MEMO_LISTE, params.toString()); } catch { /* stockage indisponible */ }
  }, [params]);

  const avecQualite = !!donnees?.colonnes?.dossier;
  const vues = VUES.filter((v) => !v.qualite || avecQualite);
  const departements = useMemo(() => (options?.departements || []).filter((d) => !params.get('site_id') || d.site_id === params.get('site_id')), [options, params]);
  const services = useMemo(() => (options?.services || []).filter((s) => !params.get('departement_id') || s.departement_id === params.get('departement_id')), [options, params]);

  const ouvrir = (l) => { if (l.ouvrable) navigate(`/employes/${l.id}`); };
  const toucheLigne = (e, l) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); ouvrir(l); } };

  const exporter = async () => {
    setExportEnCours(true);
    try {
      const query = { ...Object.fromEntries(params.entries()), taille: 100 };
      const tout = [];
      for (let p = 1; p <= 30; p += 1) {
        const { data } = await axios.get('/api/salaries', { params: { ...query, page: p } });
        tout.push(...data.lignes);
        if (p >= data.pages) break;
      }
      const ws = XLSX.utils.json_to_sheet(tout.map((l) => ({
        Matricule: l.matricule, Nom: l.nom, 'Prénom': l.prenom, 'Poste ou métier': l.intitule || '',
        Service: l.service || '', 'Département': l.departement || '', Site: l.site?.nom || '',
        'Métier': l.metier || '', Statut: LIBELLE_STATUT[l.statut] || l.statut, Contrat: l.type_contrat || '', 'Date d\'entrée': formaterDate(l.date_embauche) || '', 'Date de sortie': formaterDate(l.date_sortie) || '',
        'Fin de contrat': formaterDate(l.date_fin_contrat) || '', Responsable: l.responsable ? nomComplet(l.responsable) : '',
        ...(avecQualite ? { Dossier: { complet: 'Complet', a_completer: 'À compléter', bloquant: 'Bloquant', clos: 'Clos' }[l.dossier.etat] } : {}),
      })));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Salariés');
      XLSX.writeFile(wb, `Salaries_SOGAS_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch {
      setErreur('L\'export n\'a pas pu être réalisé.');
    } finally {
      setExportEnCours(false);
    }
  };

  const compteurs = donnees?.compteurs || {};
  const lignes = donnees?.lignes || [];

  // Affichage liste / cartes, mémorisé pour ce navigateur (préférence de confort uniquement).
  const [affichage, setAffichage] = useState(() => { try { return localStorage.getItem(MEMO_AFFICHAGE) || 'liste'; } catch { return 'liste'; } });
  const choisirAffichage = (a) => { setAffichage(a); try { localStorage.setItem(MEMO_AFFICHAGE, a); } catch { /* stockage indisponible */ } };

  // Menu « Filtres » ouvert depuis la barre de recherche (fermeture au clic extérieur et à Échap).
  const [filtresOuverts, setFiltresOuverts] = useState(false);
  const refRecherche = useRef(null);
  useEffect(() => {
    if (!filtresOuverts) return undefined;
    const clic = (e) => { if (refRecherche.current && !refRecherche.current.contains(e.target)) setFiltresOuverts(false); };
    const echap = (e) => { if (e.key === 'Escape') setFiltresOuverts(false); };
    document.addEventListener('mousedown', clic);
    document.addEventListener('keydown', echap);
    return () => { document.removeEventListener('mousedown', clic); document.removeEventListener('keydown', echap); };
  }, [filtresOuverts]);

  // Filtres actifs affichés en étiquettes dans la barre de recherche.
  const libelleOption = (liste, id) => (liste || []).find((o) => o.id === id)?.libelle || 'Sélection';
  const facettes = [
    params.get('site_id') && { cle: 'site_id', type: 'Site', valeur: libelleOption(options?.sites, params.get('site_id')), effacer: { site_id: '', departement_id: '', service_id: '' } },
    params.get('departement_id') && { cle: 'departement_id', type: 'Département', valeur: libelleOption(options?.departements, params.get('departement_id')), effacer: { departement_id: '', service_id: '' } },
    params.get('service_id') && { cle: 'service_id', type: 'Service', valeur: libelleOption(options?.services, params.get('service_id')), effacer: { service_id: '' } },
    params.get('metier_id') && { cle: 'metier_id', type: 'Métier', valeur: libelleOption(options?.metiers, params.get('metier_id')), effacer: { metier_id: '' } },
    params.get('type_contrat') && { cle: 'type_contrat', type: 'Contrat', valeur: params.get('type_contrat'), effacer: { type_contrat: '' } },
    params.get('dossier') && { cle: 'dossier', type: 'Dossier', valeur: LIBELLE_DOSSIER[params.get('dossier')] || params.get('dossier'), effacer: { dossier: '' } },
    tri !== 'nom' && { cle: 'tri', type: 'Tri', valeur: TRIS.find((t) => t.valeur === tri)?.libelle || tri, effacer: { tri: '' } },
  ].filter(Boolean);

  const page = donnees?.page || 1;
  const pages = donnees?.pages || 1;
  const taille = donnees?.taille || 25;
  const total = donnees?.total || 0;
  const resumePage = total ? `${(page - 1) * taille + 1}–${Math.min(total, page * taille)} / ${total}` : '0 / 0';
  const enCartes = affichage === 'cartes';

  return (
    <Layout>
      <div className="rh-page rh-page-large">
        {/* Bandeau de commande : création et titre, recherche et filtres, pagination et affichage */}
        <div className="rh-commande">
          <div className="rh-commande-gauche">
            {peutCreer && <Bouton variante="primaire" Icone={UserPlus} onClick={() => navigate('/employes/nouveau')}>Nouveau salarié</Bouton>}
            <h1 className="rh-titre">{titre}</h1>
            <MenuActions iconeSeule gauche libelle="Autres actions" Icone={EllipsisVertical} items={[
              { libelle: exportEnCours ? 'Export en cours…' : 'Exporter la vue (Excel)', Icone: Download, onClick: () => { if (!exportEnCours && total) exporter(); } },
              peutCreer && { libelle: 'Référentiel des métiers', Icone: Layers, onClick: () => navigate('/employes/metiers') },
            ]} />
          </div>

          <div className="rh-commande-centre" ref={refRecherche} style={{ position: 'relative' }}>
            <div className="rh-barre-recherche">
              <Search size={16} aria-hidden="true" />
              {facettes.map((f) => (
                <span key={f.cle} className="rh-facette" title={`${f.type} : ${f.valeur}`}>
                  <span><b>{f.type}</b> {f.valeur}</span>
                  <button type="button" onClick={() => majParams(f.effacer)} aria-label={`Retirer le filtre ${f.type}`}><X size={13} aria-hidden="true" /></button>
                </span>
              ))}
              <input type="search" value={saisie} onChange={(e) => setSaisie(e.target.value)} placeholder="Nom, prénom, matricule ou poste"
                aria-label="Rechercher un salarié" />
              <button type="button" className="rh-bouton-filtres" aria-expanded={filtresOuverts} aria-haspopup="dialog" onClick={() => setFiltresOuverts((o) => !o)}>
                <SlidersHorizontal size={15} aria-hidden="true" />Filtres{facettes.length ? ` (${facettes.length})` : ''}
              </button>
            </div>
            {filtresOuverts && (
              <div className="rh-panneau-filtres" role="dialog" aria-label="Filtres de la liste">
                <div className="rh-panneau-filtres-titre">
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Filter size={13} aria-hidden="true" />Filtrer et trier</span>
                  {(filtresActifs || tri !== 'nom') && <Bouton petit variante="lien" Icone={RotateCcw} onClick={() => { setSaisie(''); majParams({ ...Object.fromEntries(FILTRES.map((f) => [f, ''])), tri: '' }); }}>Tout effacer</Bouton>}
                </div>
                {(options?.sites?.length || 0) > 1 && (
                  <label>Site<Selecteur sansPrefixe libelle="Site" valeur={params.get('site_id') || ''} options={options.sites.map((s) => ({ valeur: s.id, libelle: s.libelle }))}
                    onChange={(v) => majParams({ site_id: v, departement_id: '', service_id: '' })} /></label>
                )}
                {departements.length > 0 && (
                  <label>Département<Selecteur sansPrefixe libelle="Département" valeur={params.get('departement_id') || ''} options={departements.map((d) => ({ valeur: d.id, libelle: d.libelle }))}
                    onChange={(v) => majParams({ departement_id: v, service_id: '' })} /></label>
                )}
                {services.length > 0 && (
                  <label>Service<Selecteur sansPrefixe libelle="Service" valeur={params.get('service_id') || ''} options={services.map((s) => ({ valeur: s.id, libelle: s.libelle }))}
                    onChange={(v) => majParams({ service_id: v })} /></label>
                )}
                {(options?.metiers?.length || 0) > 0 && (
                  <label>Métier<Selecteur sansPrefixe libelle="Métier" valeur={params.get('metier_id') || ''} options={options.metiers.map((m) => ({ valeur: m.id, libelle: m.libelle }))}
                    onChange={(v) => majParams({ metier_id: v })} /></label>
                )}
                <label>Contrat<Selecteur sansPrefixe libelle="Contrat" valeur={params.get('type_contrat') || ''} options={(options?.contrats || []).map((c) => ({ valeur: c, libelle: c }))}
                  onChange={(v) => majParams({ type_contrat: v })} /></label>
                {avecQualite && (
                  <label>Dossier<Selecteur sansPrefixe libelle="Dossier" valeur={params.get('dossier') || ''} options={Object.entries(LIBELLE_DOSSIER).map(([valeur, libelle]) => ({ valeur, libelle }))}
                    onChange={(v) => majParams({ dossier: v })} /></label>
                )}
                <label>Trier par
                  <select className="rh-champ-saisie" aria-label="Trier par" value={tri} onChange={(e) => majParams({ tri: e.target.value === 'nom' ? '' : e.target.value })}>
                    {TRIS.filter((t) => !t.qualite || avecQualite).map((t) => <option key={t.valeur} value={t.valeur}>{t.libelle}</option>)}
                  </select>
                </label>
              </div>
            )}
          </div>

          <div className="rh-commande-droite">
            <span className="rh-pager" aria-live="polite">
              {resumePage}
              <span className="rh-pager-groupe">
                <Bouton variante="discret" Icone={ChevronLeft} onClick={() => majParams({ page: page - 1 }, true)} disabled={page <= 1} aria-label="Page précédente" />
                <Bouton variante="discret" Icone={ChevronRight} onClick={() => majParams({ page: page + 1 }, true)} disabled={page >= pages} aria-label="Page suivante" />
              </span>
            </span>
            <span className="rh-bascule" role="group" aria-label="Affichage">
              <button type="button" aria-pressed={!enCartes} onClick={() => choisirAffichage('liste')} aria-label="Afficher en liste" title="Liste"><List size={16} aria-hidden="true" /></button>
              <button type="button" aria-pressed={enCartes} onClick={() => choisirAffichage('cartes')} aria-label="Afficher en cartes" title="Cartes"><LayoutGrid size={16} aria-hidden="true" /></button>
            </span>
          </div>
        </div>

        <div className="rh-mise-en-page-liste">
          {/* Panneau latéral « Situation » (bureau) */}
          <aside className="rh-panneau-lateral" aria-label="Situation des salariés">
            <div className="rh-panneau-titre"><Users size={15} aria-hidden="true" />Situation</div>
            <div className="rh-vues" role="group" aria-label="Vues de la liste">
              {vues.map((v) => (
                <button key={v.cle} type="button" className="rh-vue" aria-pressed={vue === v.cle}
                  onClick={() => majParams({ vue: v.cle === 'actifs' ? '' : v.cle })}>
                  <span>{v.libelle}</span>
                  {typeof compteurs[v.cle] === 'number' && <span className="rh-vue-compte">{compteurs[v.cle]}</span>}
                </button>
              ))}
            </div>
            {donnees?.population?.libelle && <p className="rh-panneau-note">Vous voyez : {donnees.population.libelle}</p>}
          </aside>

          <section aria-label="Salariés" className={enCartes ? 'rh-vue-grille-active' : undefined}>
            {/* Situation sur mobile : une liste déroulante à la place du panneau */}
            <div className="rh-situation-mobile">
              <select className="rh-champ-saisie" aria-label="Situation" value={vue} onChange={(e) => majParams({ vue: e.target.value === 'actifs' ? '' : e.target.value })}>
                {vues.map((v) => <option key={v.cle} value={v.cle}>{v.libelle}{typeof compteurs[v.cle] === 'number' ? ` (${compteurs[v.cle]})` : ''}</option>)}
              </select>
              {donnees?.population?.libelle && <p className="rh-cellule-secondaire" style={{ margin: '6px 2px 0' }}>Vous voyez : {donnees.population.libelle}</p>}
            </div>

            {erreur && <BandeauInfo ton="danger" titre="Une erreur est survenue">{erreur}</BandeauInfo>}

            {chargement && !donnees ? (
              <div className="rh-conteneur-tableau" aria-busy="true" style={{ padding: 16 }}>
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '10px 0' }}>
                    <Squelette largeur={36} hauteur={36} style={{ borderRadius: '50%' }} />
                    <div style={{ flex: 1 }}><Squelette largeur="40%" /><Squelette largeur="25%" hauteur={11} style={{ marginTop: 6 }} /></div>
                  </div>
                ))}
              </div>
            ) : lignes.length === 0 ? (
              <div className="rh-conteneur-tableau">
                {filtresActifs || vue !== 'actifs' ? (
                  <EtatVide Icone={SearchX} titre="Aucun salarié ne correspond à ces critères"
                    texte="Modifiez la recherche ou les filtres, ou revenez à la vue des actifs."
                    action={<Bouton Icone={RotateCcw} onClick={() => { setSaisie(''); setParams(new URLSearchParams(), { replace: true }); }}>Réinitialiser la liste</Bouton>} />
                ) : (
                  <EtatVide Icone={Users} titre="Aucun salarié dans votre périmètre"
                    texte="Si vous pensez devoir voir des salariés ici, rapprochez-vous du service RH." />
                )}
              </div>
            ) : enCartes ? (
              <>
                <ul className="rh-grille-cartes" aria-busy={chargement} style={{ opacity: chargement ? 0.6 : 1 }}>
                  {lignes.map((l) => <CarteSalarie key={l.id} l={l} avecQualite={avecQualite} onOuvrir={ouvrir} />)}
                </ul>
                <Pagination page={donnees.page} pages={donnees.pages} total={donnees.total} taille={donnees.taille}
                  onPage={(p) => majParams({ page: p }, true)} onTaille={(t) => majParams({ taille: t === 25 ? '' : t })} />
              </>
            ) : (
              <div className="rh-conteneur-tableau" aria-busy={chargement}>
                <table className="rh-tableau rh-tableau-bureau" style={{ opacity: chargement ? 0.6 : 1 }}>
                  <thead>
                    <tr>
                      <th scope="col">Salarié</th>
                      <th scope="col">Poste et affectation</th>
                      <th scope="col">Situation</th>
                      <th scope="col" className="rh-col-optionnelle">Responsable</th>
                      {avecQualite && <th scope="col">Dossier</th>}
                      <th scope="col" style={{ width: 32 }}><span style={{ position: 'absolute', left: -9999 }}>Ouvrir</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {lignes.map((l) => (
                      <tr key={l.id} className={`rh-ligne ${l.ouvrable ? '' : 'rh-ligne-inactive'}`} tabIndex={l.ouvrable ? 0 : -1}
                        role={l.ouvrable ? 'link' : undefined} aria-label={l.ouvrable ? `Ouvrir le dossier de ${nomComplet(l)}` : undefined}
                        title={l.ouvrable ? undefined : 'La fiche de ce salarié ne vous est pas accessible'}
                        onClick={() => ouvrir(l)} onKeyDown={(e) => toucheLigne(e, l)}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <Avatar chemin={l.photo_url} prenom={l.prenom} nom={l.nom} taille={32} />
                            <div style={{ minWidth: 0 }}>
                              <div className="rh-cellule-principale">{nomComplet(l)}</div>
                              <div className="rh-cellule-secondaire">{l.matricule}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div style={{ color: l.intitule && l.intitule_source !== 'libelle' ? 'inherit' : 'var(--sg-texte-4)' }} title={l.intitule_source === 'libelle' ? 'Libellé de l\'ancienne fiche, non rattaché à un métier' : undefined}>{l.intitule || 'Métier ou poste non renseigné'}</div>
                          <div className="rh-cellule-secondaire">{affectation(l)}</div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <BadgeStatut discret statut={l.statut} />
                            <span className="rh-cellule-secondaire" style={{ marginTop: 0 }}>{situationContrat(l)}</span>
                          </div>
                        </td>
                        <td className="rh-col-optionnelle">
                          {l.responsable ? nomComplet(l.responsable) : <span style={{ color: 'var(--sg-alerte)' }}>{l.responsable_vacant ? 'Poste vacant' : 'À définir'}</span>}
                        </td>
                        {avecQualite && <td><BadgeDossier discret etat={l.dossier.etat} nbBloquant={l.dossier.nb_bloquant} nbACompleter={l.dossier.nb_a_completer} /></td>}
                        <td style={{ color: 'var(--sg-texte-4)' }}>{l.ouvrable ? <ChevronRight size={16} aria-hidden="true" /> : <Lock size={14} aria-hidden="true" />}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <ul className="rh-cartes-liste" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {lignes.map((l) => (
                    <li key={l.id} className={`rh-ligne ${l.ouvrable ? '' : 'rh-ligne-inactive'}`} tabIndex={l.ouvrable ? 0 : -1}
                      role={l.ouvrable ? 'link' : undefined} aria-label={l.ouvrable ? `Ouvrir le dossier de ${nomComplet(l)}` : undefined}
                      onClick={() => ouvrir(l)} onKeyDown={(e) => toucheLigne(e, l)}
                      style={{ display: 'flex', gap: 12, padding: '14px 16px', borderBottom: '1px solid var(--sg-bord)', alignItems: 'flex-start' }}>
                      <Avatar chemin={l.photo_url} prenom={l.prenom} nom={l.nom} taille={40} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="rh-cellule-principale">{nomComplet(l)}</div>
                        <div className="rh-cellule-secondaire">{l.intitule || 'Métier ou poste non renseigné'} · {l.matricule}</div>
                        <div className="rh-cellule-secondaire">{affectation(l)}</div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                          <BadgeStatut statut={l.statut} />
                          {avecQualite && <BadgeDossier etat={l.dossier.etat} nbBloquant={l.dossier.nb_bloquant} nbACompleter={l.dossier.nb_a_completer} />}
                        </div>
                      </div>
                      {l.ouvrable && <ChevronRight size={16} color="var(--sg-texte-4)" aria-hidden="true" style={{ marginTop: 10 }} />}
                    </li>
                  ))}
                </ul>

                <Pagination page={donnees.page} pages={donnees.pages} total={donnees.total} taille={donnees.taille}
                  onPage={(p) => majParams({ page: p }, true)} onTaille={(t) => majParams({ taille: t === 25 ? '' : t })} />
              </div>
            )}
          </section>
        </div>
      </div>
    </Layout>
  );
}
